/** Page Données : sauvegarde JSON complète + export/import CSV par entité. */

import { useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { todayKey } from '@/domain';
import {
  exportBackup,
  exportConsumptionsCsv,
  exportExerciseCsv,
  exportFoodsCsv,
  exportWaterCsv,
  exportWeightsCsv,
  type ImportReport,
  importBackup,
  importConsumptionsCsv,
  importExerciseCsv,
  importFoodsCsv,
  importWaterCsv,
  importWeightsCsv,
} from '@/lib/data-io';

function download(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface Section {
  key: string;
  title: string;
  hint: string;
  exporter: () => Promise<string>;
  importer: (text: string) => Promise<ImportReport>;
}

const SECTIONS: Section[] = [
  {
    key: 'weights',
    title: 'Pesées',
    hint: 'colonnes : date, weight_kg',
    exporter: exportWeightsCsv,
    importer: importWeightsCsv,
  },
  {
    key: 'foods',
    title: 'Aliments personnels',
    hint: 'colonnes : name + composition /100g',
    exporter: exportFoodsCsv,
    importer: importFoodsCsv,
  },
  {
    key: 'consumptions',
    title: 'Consommations',
    hint: 'datetime, meal, grams, food_name + composition (aliments manquants créés)',
    exporter: exportConsumptionsCsv,
    importer: importConsumptionsCsv,
  },
  {
    key: 'water',
    title: 'Hydratation',
    hint: 'colonnes : datetime, ml',
    exporter: exportWaterCsv,
    importer: importWaterCsv,
  },
  {
    key: 'exercise',
    title: 'Sport',
    hint: 'colonnes : datetime, activity, kcal_burned, duration_min, note',
    exporter: exportExerciseCsv,
    importer: importExerciseCsv,
  },
];

export function DataPage() {
  const qc = useQueryClient();
  const [reports, setReports] = useState<Record<string, ImportReport>>({});
  const [busy, setBusy] = useState('');

  async function handleExport(s: Section) {
    setBusy(s.key);
    try {
      const csv = await s.exporter();
      download(`kcal-${s.key}-${todayKey()}.csv`, csv);
    } finally {
      setBusy('');
    }
  }

  async function handleImport(s: Section, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de réimporter le même fichier
    if (!file) return;
    setBusy(s.key);
    try {
      const text = await file.text();
      const report = await s.importer(text);
      setReports((r) => ({ ...r, [s.key]: report }));
      await qc.invalidateQueries();
    } finally {
      setBusy('');
    }
  }

  const [backupReport, setBackupReport] = useState<ImportReport | null>(null);

  async function handleBackupExport() {
    setBusy('backup');
    try {
      const json = await exportBackup();
      download(`kcal-sauvegarde-${todayKey()}.json`, json);
    } finally {
      setBusy('');
    }
  }

  async function handleBackupImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy('backup');
    try {
      const report = await importBackup(await file.text());
      setBackupReport(report);
      await qc.invalidateQueries();
    } catch (err) {
      setBackupReport({
        entity: 'Sauvegarde',
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [(err as Error).message],
      });
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Données</h1>
          <p>Sauvegarde complète (JSON) ou export/import par type (CSV). Imports idempotents.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>Sauvegarde complète (JSON)</h3>
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Profil, pesées, paliers, aliments personnels, consommations, hydratation, sport et favoris
          en un seul fichier.
        </p>
        <div className="toolbar" style={{ margin: '0.5rem 0' }}>
          <button type="button" disabled={busy === 'backup'} onClick={handleBackupExport}>
            Télécharger la sauvegarde
          </button>
          <label
            className="ghost"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.55rem 1.1rem',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Restaurer…
            <input
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              disabled={busy === 'backup'}
              onChange={handleBackupImport}
            />
          </label>
        </div>
        {backupReport && (
          <div style={{ fontSize: '0.85rem' }}>
            <p style={{ margin: '0.3rem 0' }}>
              <span className="badge good">{backupReport.created} créés</span>{' '}
              <span className="badge">
                {backupReport.updated} maj · {backupReport.skipped} ignorés
              </span>{' '}
              {backupReport.errors.length > 0 && (
                <span className="badge bad">{backupReport.errors.length} erreurs</span>
              )}
            </p>
            {backupReport.errors.slice(0, 5).map((err, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: liste d'erreurs figée (jamais réordonnée), l'index est un identifiant stable ici.
              <p key={`${i}-${err}`} className="error" style={{ margin: '0.15rem 0' }}>
                {err}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="grid two">
        {SECTIONS.map((s) => {
          const report = reports[s.key];
          return (
            <div className="card" key={s.key}>
              <h3>{s.title}</h3>
              <p className="muted" style={{ fontSize: '0.8rem' }}>
                {s.hint}
              </p>
              <div className="toolbar" style={{ margin: '0.5rem 0' }}>
                <button type="button" disabled={busy === s.key} onClick={() => handleExport(s)}>
                  Exporter
                </button>
                <label
                  className="ghost"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.55rem 1.1rem',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Importer…
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    disabled={busy === s.key}
                    onChange={(e) => handleImport(s, e)}
                  />
                </label>
              </div>
              {report && (
                <div style={{ fontSize: '0.85rem' }}>
                  <p style={{ margin: '0.3rem 0' }}>
                    <span className="badge good">{report.created} créés</span>{' '}
                    <span className="badge">{report.skipped} ignorés</span>{' '}
                    {report.errors.length > 0 && (
                      <span className="badge bad">{report.errors.length} erreurs</span>
                    )}
                  </p>
                  {report.errors.slice(0, 5).map((err, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: liste d'erreurs figée (jamais réordonnée), l'index est un identifiant stable ici.
                    <p key={`${i}-${err}`} className="error" style={{ margin: '0.15rem 0' }}>
                      {err}
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
